/**
 * <g-kanban> - Kanban Board Container
 *
 * Manages SortableJS across all child g-column elements.
 * Emits events for card movements and clicks.
 *
 * @example
 * <g-kanban @card-moved="handleMove($event.detail)" @card-clicked="handleClick($event.detail)">
 *   <g-column status="todo" label="To Do" color="purple">
 *     <g-card card-id="1">...</g-card>
 *   </g-column>
 * </g-kanban>
 *
 * @attr {boolean} loading - presence = skeleton columns in place of the board
 * @attr {string} pin-key - Which saved pins this board's cards read (default: the board's id, else "board").
 *   Cards are pinnable by the viewer (a pin on hover, kept in localStorage per page); see g-card.
 * @attr {boolean} no-pins - presence = cards on this board cannot be pinned
 *
 * @fires card-moved - When a card is dropped in a new position
 * @fires card-clicked - When a card is clicked (for edit modal)
 * @fires card-pinned - { cardId, pinned } when the viewer pins or unpins a card
 */
import { ensureDragStyles, DRAG_CLASSES } from '../util/drag.js';
import { neighbours } from '../util/order.js';

// While loading, the host's own columns (Alpine x-for output) are hidden and
// a skeleton board stands in. A stylesheet rule rather than toggling each
// child: Alpine adds and removes those children on its own schedule.
function ensureSkeletonStyles() {
  if (document.getElementById('g-kanban-skeleton-styles')) return;
  const style = document.createElement('style');
  style.id = 'g-kanban-skeleton-styles';
  style.textContent = 'g-kanban[loading] > :not(.g-kanban-skeleton){display:none!important}';
  document.head.appendChild(style);
}

function skeletonColumn(cards) {
  return `<div class="flex-1 min-w-[200px] rounded-box bg-base-200/60 p-3 space-y-3">
    <div class="skeleton h-4 w-24"></div>
    ${Array.from({ length: cards }, () => '<div class="rounded-box bg-base-100 p-3 space-y-2 shadow-sm"><div class="skeleton h-4 w-3/4"></div><div class="skeleton h-3 w-1/2"></div></div>').join('')}
  </div>`;
}

export class GKanban extends HTMLElement {
  constructor() {
    super();
    this.sortableInstances = [];
    this._initTimeout = null;
    this._isDragging = false;
  }

  static get observedAttributes() {
    return ['loading'];
  }

  attributeChangedCallback(name) {
    if (name === 'loading') this._syncSkeleton();
  }

  _syncSkeleton() {
    const loading = this.hasAttribute('loading');
    if (loading && !this._skeleton && this.isConnected) {
      ensureSkeletonStyles();
      this._skeleton = document.createElement('div');
      this._skeleton.className = 'g-kanban-skeleton flex gap-4 w-full';
      this._skeleton.setAttribute('aria-busy', 'true');
      this._skeleton.innerHTML = [3, 2, 1].map(skeletonColumn).join('');
      this.appendChild(this._skeleton);
    } else if (!loading && this._skeleton) {
      this._skeleton.remove();
      this._skeleton = null;
    }
  }

  connectedCallback() {
    ensureDragStyles();
    this._syncSkeleton();
    // Set up container styles via Tailwind classes.
    //
    // Contained, Trello-style: the board takes the height its container gives
    // it (`h-full min-h-0`), each column scrolls its own cards, and the
    // sideways scrollbar is at the board's bottom edge — on screen. Without a
    // height the board grew to its longest column, so in a panel or a page the
    // sideways scrollbar sat under the last card (staging qa-tracker,
    // 2026-10-13). A container with no settled height still gets the old
    // behaviour: h-full resolves to auto and the board grows.
    this.classList.add('flex', 'gap-4', 'p-4', 'overflow-x-auto', 'overflow-y-hidden', 'h-full', 'min-h-0');

    // Wait for child elements to be parsed and rendered
    setTimeout(() => {
      this.initSortable();
    }, 100);

    // Re-initialize when children change (for Alpine x-for updates)
    // Use debounce to avoid constant re-init
    this.observer = new MutationObserver(() => {
      this.debouncedInit();
    });
    this.observer.observe(this, { childList: true, subtree: true });

    // Delegate card clicks
    this.addEventListener('click', this.handleClick.bind(this));
  }

  disconnectedCallback() {
    this.destroy();
    this.observer?.disconnect();
    if (this._initTimeout) clearTimeout(this._initTimeout);
  }

  debouncedInit() {
    // Don't reinit while dragging
    if (this._isDragging) return;

    if (this._initTimeout) clearTimeout(this._initTimeout);
    this._initTimeout = setTimeout(() => {
      if (!this._isDragging) {
        this.initSortable();
      }
    }, 100);
  }

  destroy() {
    this.sortableInstances.forEach(s => s.destroy());
    this.sortableInstances = [];
  }

  initSortable() {
    // Clean up existing instances
    this.destroy();

    // Check if Sortable is available
    if (typeof Sortable === 'undefined') {
      console.error('[g-kanban] SortableJS not found. Include it before using g-kanban.');
      return;
    }

    const group = this.getAttribute('group') || 'g-kanban-shared';
    const columns = this.querySelectorAll('g-column');
    const distribute = columns.length <= 3;

    console.log('[g-kanban] Initializing Sortable on', columns.length, 'columns, distribute:', distribute);

    // Toggle column sizing based on count
    columns.forEach(column => {
      if (distribute) {
        column.classList.add('flex-1', 'min-w-[200px]');
        column.classList.remove('w-[300px]');
      } else {
        column.classList.remove('flex-1', 'min-w-[200px]');
        column.classList.add('w-[300px]');
      }
    });

    columns.forEach(column => {
      const cards = column.querySelectorAll('g-card');
      console.log('[g-kanban] Column', column.getAttribute('status'), 'has', cards.length, 'cards');

      const sortable = new Sortable(column, {
        group: group,
        animation: 150,
        ...DRAG_CLASSES,
        draggable: 'g-card',
        filter: '.g-column-header',
        onStart: (evt) => {
          this._isDragging = true;
          console.log('[g-kanban] Drag started:', evt.item.getAttribute('card-id'));
        },
        onEnd: (evt) => {
          this._isDragging = false;
          console.log('[g-kanban] Drag ended:', evt.item.getAttribute('card-id'));
          this.handleDragEnd(evt);
        },
        onUnchoose: () => {
          this._isDragging = false;
        }
      });

      this.sortableInstances.push(sortable);
    });
  }

  handleDragEnd(evt) {
    const card = evt.item;
    const cardId = card.getAttribute('card-id');
    const fromColumn = evt.from.closest('g-column');
    const toColumn = evt.to.closest('g-column');
    // The column's order after the drop, read from the DOM — the only place it
    // exists until the host re-renders from its saved order.
    const ids = toColumn
      ? [...toColumn.querySelectorAll(':scope > g-card')].map(c => c.getAttribute('card-id'))
      : [];

    this.dispatchEvent(new CustomEvent('card-moved', {
      detail: {
        cardId,
        fromStatus: fromColumn?.getAttribute('status'),
        toStatus: toColumn?.getAttribute('status'),
        // Card positions, not child positions. Sortable's oldIndex/newIndex
        // count the column header too, so they were one too high — a card
        // dragged up one slot saved where it already was.
        oldIndex: evt.oldDraggableIndex,
        newIndex: evt.newDraggableIndex,
        ids,
        // Where the card landed relative to the cards around it, so a host
        // can place it in the whole collection's order (shared with a task
        // list over the same records) instead of numbering one column.
        ...neighbours(ids, cardId)
      },
      bubbles: true,
      composed: true
    }));
  }

  handleClick(e) {
    const card = e.target.closest('g-card');
    // Don't fire if clicking a button or interactive element inside the card
    if (card && !e.target.closest('button, a, input, select, textarea')) {
      this.dispatchEvent(new CustomEvent('card-clicked', {
        detail: {
          cardId: card.getAttribute('card-id'),
          card: card
        },
        bubbles: true,
        composed: true
      }));
    }
  }
}

if (!customElements.get('g-kanban')) {
  customElements.define('g-kanban', GKanban);
}
